import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import { useNavigate } from "react-router";
import CounterField from "../CounterField";
import { FiMinus, FiPlus } from "react-icons/fi";
import { useRouter } from "@/app/hooks/useRouter";
import { useSearchParams } from "react-router-dom";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useEffect, useRef, useState } from "react";
import Input from "@/app/components/ui/Inputs/Input";
import { RegisterOptions, useForm } from "react-hook-form";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import useFormValidations from "@/app/hooks/useFormValidations";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import { useOrderManagementClinicListAPI } from "@/app/hooks/api_hooks/orders/useOrderClinicListAPI";
import ServiceSubServiceSelectMenu from "@/app/components/ui/SelectMenus/ServiceSubServiceSelectMenu";
import { useConfigurationEquipmentDetailAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationEquipmentDetailAPI";
import { useConfigurationEquipmentUpdateAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationEquipmentUpdateAPI";
import { useConfigurationEquipmentOnboardAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationEquipmentOnboardAPI";
import { useConfigurationEquipmentServiceListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationEquipmentServicesListAPI";
import { buildEquipmentPayload, getEmptyServices, getServiceValidationError, mapSelectedServices, mapServicesToSelectItems, updateClinicCount } from "@/app/hooks/custom_hooks";
interface IEquipmentOnboardFormFields {
  equipmentName: string;
  services: ISelectMenuItemData[];
  clinic: string;
}

interface IClinicDetailsFormField {
  clinic_id: string;
  clinic_name?: string;
  equipment_count: number;
}

const ROW_CONTROL_BUTTON =
  "flex justify-center items-center bg-gray-50 border border-gray-200 rounded-md w-9 h-9 text-blue-600 text-xl transition-colors hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed";

const ConfigurationEquipmentOnboard = () => {
  const clinicServicesFetched = useRef(false);
  const detailsFetched = useRef(false);
  const [params, _setParams] = useSearchParams();
  const [isForUpdate, setIsForUpdate] = useState<boolean>(false);
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const [clinicDetails, setClinicDetails] = useState<IClinicDetailsFormField[]>([
    {
      clinic_id: "",
      clinic_name: "",
      equipment_count: 0,
    },
  ]);
  const clinicListAPI = useOrderManagementClinicListAPI();
  const fetchEquipmentService = useConfigurationEquipmentServiceListAPI();
  const { newEquipmentId, execute: onboardNewEquipment } = useConfigurationEquipmentOnboardAPI();
  const { updatedEquipmentId, execute: updateEquipment } = useConfigurationEquipmentUpdateAPI();
  const { equipmentDetails, execute: fetchEquipmentDetails } = useConfigurationEquipmentDetailAPI();
  const { forAllAcceptWithOutStartWithDotAndSpace } = useFormValidations();
  const routeIdOrder = getRouteKey("ORDER_MANAGEMENT", "id");
  const { routeState: routeStateConfig } = useRouteInfo(routeIdOrder);
  const { org_services: orgServices } = useAppSelector((state) => state.staticAPI);
  const equipmentServiceOrgItem = mapServicesToSelectItems(orgServices);
  const clinicsOrg = routeStateConfig?.state?.clinicList?.results;
  const clinicsOrgItem = useSelectMenuReducer(clinicsOrg, "name", "id");

  const formHook = useForm<IEquipmentOnboardFormFields>({
    mode: "onChange",
    defaultValues: {
      services: [],
    },
  });

  const { register, setValue, formState } = formHook;
  const services = formHook.watch("services");

  const handleClinicSelection = (clinicId, index) => {
    const clinicName = clinicsOrgItem?.find((c) => c.id === clinicId)?.title;
    setClinicDetails((prev) =>
      prev.map((item, i) => (i === index ? { ...item, clinic_id: clinicId, clinic_name: clinicName } : item)),
    );
  };

  const Submit = (data: IEquipmentOnboardFormFields) => {
    const error = getServiceValidationError(data.services);
    if (error) {
      formHook.setError("services", {
        type: "custom",
        message: error,
      });
      return;
    }
    const payload = buildEquipmentPayload(data, clinicDetails);
    if (!isForUpdate) {
      onboardNewEquipment(payload);
    } else {
      updateEquipment({
        ...payload,
        equipment_id: params?.get("equipment_id"),
      });
    }
  };

  useEffect(() => {
    if (newEquipmentId || updatedEquipmentId) {
      navigate(getRouteKey("EQUIPMENT_SETUP", "url"));
    }
  }, [newEquipmentId, updatedEquipmentId]);

  useEffect(() => {
    if (!clinicServicesFetched.current) {
      clinicListAPI();
      fetchEquipmentService();
      clinicServicesFetched.current = true;
    }
  }, []);

  useEffect(() => {
    if (equipmentDetails) {
      setValue("equipmentName", equipmentDetails?.equipment_name);
      if (equipmentDetails?.clinic_equipment_details?.length > 0)
        setClinicDetails(
          equipmentDetails?.clinic_equipment_details?.map((cd) => ({
            clinic_id: cd?.clinic_id,
            equipment_count: parseInt(cd?.equipment_count),
          })),
        );
      setValue("services", mapSelectedServices(equipmentServiceOrgItem, equipmentDetails.services_data));
      setIsForUpdate(true);
    }
  }, [equipmentDetails]);

  useEffect(() => {
    if (params?.get("equipment_id") && !detailsFetched.current) {
      fetchEquipmentDetails(params?.get("equipment_id"));
      detailsFetched.current = true;
    }
  }, [params?.get("equipment_id")]);

  const handleCancel = () => {
    formHook?.reset();
    navigate(getRouteKey("EQUIPMENT_SETUP", "url"));
  };

  const getAvailableClinics = () => clinicsOrgItem.filter((c) => !clinicDetails.some((d) => d.clinic_id === c.id));

  const handleAddClinic = (e) => {
    e.preventDefault();
    const lastClinic = clinicDetails[clinicDetails.length - 1];
    if (lastClinic?.equipment_count === 0) return;
    setClinicDetails((prev) => [...prev, { clinic_id: "", equipment_count: 0, clinic_name: "" }]);
  };

  const handleRemoveClinic = (e) => {
    e.preventDefault();
    if (clinicDetails.length === 1) return;
    setClinicDetails((prev) => prev.slice(0, -1));
  };

  return (
    <div className="bg-white rounded-md py-1 pb-24">
      <div className="text-black text-base font-semibold mt-2 mx-3">Equipment Setup</div>
      <div className="mx-3">
        <form onSubmit={formHook.handleSubmit(Submit, () => getServiceValidationError(formHook.getValues("services")))}>
          <div className="justify-start items-baseline flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[300px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Name of the equipment </span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <Input
              className="border-slate-400 rounded-md w-[300px] h-9 text-sm"
              placeholder="Enter Equipment Name"
              register={register("equipmentName", {
                ...(forAllAcceptWithOutStartWithDotAndSpace.validations as RegisterOptions<
                  IEquipmentOnboardFormFields,
                  "equipmentName"
                >),
                required: true,
              })}
              fieldError={formState.errors.equipmentName}
              errorMessages={[
                { message: "Equipment name is required", type: "required" },
                forAllAcceptWithOutStartWithDotAndSpace.errors,
              ]}
            />
          </div>
          <div className="justify-start items-center flex mt-5">
            <div className="h-[38px] p-2.5 justify-start w-[300px] items-center gap-1 flex">
              <span className="text-black text-xs font-normal">Choose Services</span>
              <span className="text-rose-600 text-xs font-normal">*</span>
            </div>
            <div id="servicesDropdown" className="w-[300px]">
              <ServiceSubServiceSelectMenu
                selectItems={equipmentServiceOrgItem}
                placeholder="Select Services"
                className="text-xs"
                showDropdownIcon={true}
                errorMessages={[
                  { type: "required", message: "Please select a service and it's sub-service" },
                  {
                    type: "custom",
                    message: `Sub-services for ${getEmptyServices(services)?.join(", ")} should be selected.`,
                  },
                ]}
                fieldError={formState.errors?.services}
                register={register("services", { required: true })}
                onSelectItem={(items) => {
                  if (items) {
                    setValue("services", items);
                  }
                }}
                onSelectSubItem={(subItems) => {
                  if (subItems) {
                    const newitems = formHook.watch("services").map((s) => {
                      const { subSelectItems, ...rest } = s;
                      return { ...rest, subSelectItems: subItems[s?.id] };
                    });
                    setValue("services", newitems);
                  }
                }}
                defaultSelected={services}
              />
            </div>
          </div>
          <div className="left-0 top-[33px] justify-start items-start gap-16 inline-flex mt-6 bg-white">
            <div className="h-11 px-2.5 py-1 justify-start items-center gap-2.5 flex">
              <div className="w-[123px] text-black text-xs font-normal">Choose clinics & equipment count</div>
            </div>
            <div className="flex-col justify-start items-start gap-2.5 inline-flex">
              {/* Headers and rows share one flex column so the white card grows with the
                  content instead of the rows overflowing it. */}
              <div className="justify-start items-start gap-[23px] inline-flex">
                <div className="w-[300px] text-black text-xs font-medium leading-tight tracking-wide">Clinic</div>
                <div className="text-black text-xs font-medium leading-tight tracking-wide">Equipment Count</div>
              </div>
              <div className="flex-col justify-start items-start gap-2.5 inline-flex">
                    {clinicDetails?.length > 0 &&
                      clinicDetails?.map((clinic, i) => (
                        <div className="justify-start items-start gap-[23px] inline-flex" key={i}>
                          <div className="justify-end items-start flex">
                            <div className="w-[300px] pr-[9px] text-black text-xs font-medium leading-tight tracking-wide">
                              <NonSearchableSelectMenu
                                errorMessages={[]}
                                onSelectItem={(item) => handleClinicSelection(item?.id, i)}
                                fieldError={formState?.errors?.clinic}
                                register={register(`clinic-${i}` as "clinic", { required: false })}
                                selectItems={
                                  clinic?.clinic_id
                                    ? [...getAvailableClinics(), { id: clinic.clinic_id, title: clinic.clinic_name }]
                                    : getAvailableClinics()
                                }
                                placeholder="Select Clinic"
                                showTooltips={false}
                                showTypedErrors={false}
                                showDropdownIcon={true}
                                defaultSelected={clinicsOrgItem?.find((oc) => oc?.id === clinic?.clinic_id)}
                                listBoxClassName="w-full"
                                className="text-gray-800"
                              />
                            </div>
                          </div>
                          {/* Count and row controls stay visible without a clinic, just inert —
                              the handlers already no-op in exactly these cases. */}
                          <div className="justify-start items-center gap-2 flex">
                            <div className="h-9 justify-start items-center gap-3 flex">
                              <CounterField
                                disabled={!clinic?.clinic_id}
                                count={clinicDetails[i]?.equipment_count}
                                onIncrement={() => setClinicDetails((prev) => updateClinicCount(prev, i, (c) => c + 1))}
                                onDecrement={() =>
                                  setClinicDetails((prev) => updateClinicCount(prev, i, (c) => Math.max(c - 1, 0)))
                                }
                              />
                            </div>
                          </div>
                          {i === 0 && (
                            <>
                              <button
                                type="button"
                                onClick={handleAddClinic}
                                disabled={!clinic?.clinic_id}
                                className={ROW_CONTROL_BUTTON}
                              >
                                <FiPlus />
                              </button>
                              <button
                                type="button"
                                onClick={handleRemoveClinic}
                                disabled={!clinic?.clinic_id || clinicDetails?.length === 1}
                                className={ROW_CONTROL_BUTTON}
                              >
                                <FiMinus />
                              </button>
                            </>
                          )}
                        </div>
                      ))}
              </div>
            </div>
          </div>
          <div className="bottom-0 fixed left-0 right-0 px-10 py-3 justify-end items-center gap-4 flex border-t-2 bg-white">
            <button type="button" onClick={handleCancel} className={SECONDARY_BUTTON}>
              Cancel
            </button>
            <button type="submit" className={PRIMARY_BUTTON}>
              {equipmentDetails ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConfigurationEquipmentOnboard;
